import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { testApi } from "../../api/testApi";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { Button } from "../../components/ui/button";
import { getTestErrorDisplay } from "../../utils/apiErrorMessage";
import testNotFound from "../../assets/no-test-found.png";

export default function AssessmentWindow() {
  const navigate = useNavigate();
  const { courseId, quizId } = useParams();
  const hasStartedRef = useRef(false);
  const timerRef = useRef(null);

  if (!quizId || quizId === "null" || quizId === "undefined") {
    return (
      <div className="flex min-h-screen w-full flex-col items-center justify-center px-4 text-center">
        <img
          src={testNotFound}
          alt="Test Not Found"
          className="w-64 h-64 mb-6 opacity-90"
        />
        <div className="text-2xl font-bold text-gray-700 mb-2">
          Test Not Found
        </div>
        <div className="text-gray-500 text-lg mb-4">
          No assessment is configured for this course. Please contact your
          instructor or try again later.
        </div>
        <Button
          className="mt-2 px-8 py-2 rounded-full bg-[var(--secondary-font)] text-white hover:bg-[var(--primary-font)]"
          onClick={() => navigate(`/dashboard/courses/${courseId}`)}
        >
          Back to Course
        </Button>
      </div>
    );
  }

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Test flow state
  const [testAttempt, setTestAttempt] = useState(null);
  const [attemptId, setAttemptId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [testTitle, setTestTitle] = useState("");

  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState({
    score: 0,
    total: 0,
    passed: false,
    percentage: 0,
    correctAnswers: 0,
    wrongAnswers: 0,
  });
  const [submitting, setSubmitting] = useState(false);

  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [timerExpired, setTimerExpired] = useState(false);

  // Tab switch warning
  const [showTabWarning, setShowTabWarning] = useState(false);
  const [tabSwitchCount, setTabSwitchCount] = useState(0);

  // Initialize test attempt when component mounts
  useEffect(() => {
    const startTestAttempt = async () => {
      if (!quizId || hasStartedRef.current) return;
      hasStartedRef.current = true;

      try {
        setLoading(true);
        setError(null);

        const startResponse = await testApi.startTest({ testId: quizId });
        const attemptData = startResponse.data;
        setTestAttempt(attemptData);
        setAttemptId(attemptData.attemptId);
        setQuestions(attemptData.questions || []);
        setTestTitle(attemptData.course || "Assessment");

        // Set initial timer
        if (attemptData.timeRemaining && attemptData.timeRemaining > 0) {
          setTimeRemaining(attemptData.timeRemaining);
        }
      } catch (error) {
        console.error("Failed to start test:", error);
        setError(error || { message: "Failed to start test." });
      } finally {
        setLoading(false);
      }
    };

    startTestAttempt();
  }, [quizId]);

  // Timer countdown effect
  useEffect(() => {
    if (timeRemaining > 0 && !showResult && !submitting) {
      timerRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            setTimerExpired(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [timeRemaining, showResult, submitting]);

  // Auto-submit when timer expires
  useEffect(() => {
    if (timerExpired && !submitting && !showResult) {
      handleEndTest();
    }
  }, [timerExpired]);

  // Tab switch detection
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (
        document.hidden &&
        !showResult &&
        !showEndConfirm &&
        !submitting &&
        questions.length
      ) {
        setTabSwitchCount((prev) => {
          const nextCount = prev + 1;
          if (nextCount >= 3) {
            handleEndTest();
          } else {
            setShowTabWarning(true);
          }
          return nextCount;
        });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [questions.length, showEndConfirm, showResult, submitting]);

  function handleSelect(optionIdx) {
    setAnswers((prev) => ({ ...prev, [current]: optionIdx }));
  }

  function handleNav(idx) {
    setCurrent(idx);
  }

  function handleNext() {
    if (current < questions.length - 1) setCurrent(current + 1);
  }

  function handlePrev() {
    if (current > 0) setCurrent(current - 1);
  }

  async function submitAnswerWithRetry(attemptId, payload, retries = 2) {
    let lastError;
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        await testApi.submitAnswer(attemptId, payload);
        return;
      } catch (error) {
        lastError = error;
        if (attempt < retries) {
          await new Promise((resolve) =>
            setTimeout(resolve, 500 * (attempt + 1)),
          );
        }
      }
    }
    throw lastError;
  }

  async function handleEndTest() {
    if (!attemptId || submitting) return;

    setSubmitting(true);
    setShowEndConfirm(false);
    setTimerExpired(false);

    // Clear timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    try {
      const failedQuestionNumbers = [];

      for (const [questionIndex, optionIndex] of Object.entries(answers)) {
        const question = questions[parseInt(questionIndex, 10)];
        const selectedOption = question?.options?.[optionIndex];
        if (!question?.id || !selectedOption) continue;

        try {
          await submitAnswerWithRetry(attemptId, {
            questionId: question.id,
            userAnswer: {
              id: selectedOption.id,
              text: selectedOption.text,
            },
            timeSpent: 30,
          });
        } catch (error) {
          console.error(
            `Failed to submit answer for question ${question.id}:`,
            error,
          );
          failedQuestionNumbers.push(parseInt(questionIndex, 10) + 1);
        }
      }

      if (failedQuestionNumbers.length > 0) {
        setError(
          `Failed to save answers for question(s): ${failedQuestionNumbers.join(", ")}. Please try submitting again.`,
        );
        return;
      }

      await testApi.completeTest(attemptId);
      navigate(`/dashboard/courses/${courseId}/assessment/result/${attemptId}`);
    } catch (error) {
      console.error("Failed to complete test:", error);
      setError(error || { message: "Failed to complete test." });
    } finally {
      setSubmitting(false);
    }
  }

  const handleReAttempt = () => {
    setAnswers({});
    setCurrent(0);
    setShowResult(false);
    setTimeRemaining(0);
    setTimerExpired(false);
    setTabSwitchCount(0);
    hasStartedRef.current = false;
  };

  // Format time for display
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  if (loading) {
    return (
      <div className="flex min-h-screen w-full flex-col items-center justify-center gap-4 px-4 text-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-4 border-[var(--secondary-font)]"></div>
        <p className="text-[var(--secondary-font)] font-bold">
          Loading assessment...
        </p>
      </div>
    );
  }

  if (error || !testAttempt) {
    const display = getTestErrorDisplay(error);
    return (
      <div className="flex min-h-screen w-full flex-col items-center justify-center px-4 text-center">
        <img
          src={testNotFound}
          alt="Test Not Found"
          className="w-64 h-64 mb-6 opacity-90"
        />
        <div className="text-2xl font-bold text-gray-700 mb-2">
          {display.title}
        </div>
        <div className="text-gray-500 text-lg mb-4">
          {display.message}
        </div>
        <Button
          className="mt-2 px-8 py-2 rounded-full bg-[var(--secondary-font)] text-white hover:bg-[var(--primary-font)]"
          onClick={() => navigate(`/dashboard/courses/${courseId}`)}
        >
          Back to Course
        </Button>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-[100dvh] w-full flex-col bg-white lg:flex-row">
      {/* Sidebar */}
      <div className="flex w-full flex-col justify-between border-b border-gray-200 bg-white px-4 py-6 lg:h-full lg:w-[320px] lg:border-b-0 lg:border-r lg:px-6 lg:py-8">
        <div className="flex-1 overflow-y-auto pr-1">
          <div className="mb-2 font-sans text-lg font-semibold text-[#2B6A7F]">
            {testTitle + " Assessment"}
          </div>
          <div className="text-xs text-[#00000080] font-semibold mb-8">
            {testAttempt?.minPassPercentage || 70}% Minimum Passing Score
          </div>
          <div className="flex items-center justify-start flex-wrap gap-3">
            {questions.map((q, i) => (
              <button
                key={i}
                className={`rounded-lg text-sm w-12 h-12 font-semibold border transition-all
                  ${current === i ? "bg-[#6ea6b9] text-white border-[#6ea6b9]" : answers[i] !== undefined ? "bg-[#cbe3ed] text-[var(--secondary-font)] border-[#cbe3ed]" : "bg-[#e3eaf3] text-[#7a869a] border-[#e3eaf3]"}
                `}
                onClick={() => handleNav(i)}
                disabled={submitting}
              >
                {i + 1}
              </button>
            ))}
          </div>

          {/* Timer Display */}
        </div>

        <button
          className="mt-8 w-full py-3 rounded-xl bg-[#5D93A5] text-white font-semibold text-lg hover:bg-[var(--secondary-font)] transition disabled:opacity-50"
          onClick={() => setShowEndConfirm(true)}
          disabled={submitting}
        >
          {submitting ? "Submitting..." : "End Test"}
        </button>
      </div>

      {/* Main Content */}
      <div className="relative flex flex-1 flex-col overflow-hidden px-4 py-6 md:px-8 lg:px-10">
        <div className="flex h-full min-h-0 w-full flex-col justify-between gap-6">
          <div className="w-full flex-1 overflow-y-auto pr-1 pb-4">
            <div className="flex flex-wrap items-start justify-between gap-4 sm:flex-nowrap sm:items-center">
              <div className="text-xs text-gray-500 mb-4">
                {current + 1} out of {questions.length}
              </div>
              {timeRemaining > 0 && (
                <div className="text-right">
                  <div className="text-xs text-gray-500">Time left</div>
                  <div
                    className={`text-2xl font-bold ${timeRemaining <= 300 ? "text-red-600" : "text-[var(--secondary-font)]"}`}
                  >
                    {formatTime(timeRemaining)}
                  </div>
                  {timeRemaining <= 300 && (
                    <div className="text-xs text-red-500 mt-1">
                      {timeRemaining <= 60
                        ? "Hurry up!"
                        : "Running low on time!"}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="font-semibold text-lg mb-3 text-[#2B6A7F]">
              Question {current + 1}
            </div>
            <div className="font-medium text-[15px] mb-6 leading-relaxed text-[#000000]">
              {questions[current]?.question || "No question."}
            </div>

            {/* Display media if available */}
            {questions[current]?.imageUrl && (
              <img
                src={questions[current].imageUrl}
                alt="Question image"
                className="max-w-full h-auto mb-4 rounded-lg"
              />
            )}

            {questions[current]?.videoUrl && (
              <video
                src={questions[current].videoUrl}
                controls
                className="max-w-full h-auto mb-4 rounded-lg"
              />
            )}

            <div className="text-xs text-[#7a869a] mb-4">
              (Select one answer)
            </div>
            <div className="flex flex-col gap-3">
              {questions[current]?.options?.map((opt, idx) => (
                <label
                  key={idx}
                  className={`flex items-start gap-3 p-4 rounded-2xl border cursor-pointer transition-all
                    ${answers[current] === idx ? "bg-[#D9ECF8] !border-[#2B6A7F] border-2" : "bg-white border-[#e3eaf3] hover:border-gray-300"}
                    ${submitting ? "opacity-50 cursor-not-allowed" : ""}
                  `}
                >
                  <input
                    type="radio"
                    name={`q${current}`}
                    checked={answers[current] === idx}
                    onChange={() => handleSelect(idx)}
                    className="accent-[#2B6A7F] w-4 h-4"
                    disabled={submitting}
                  />
                  <span className="text-base text-[#2B6A7F]">
                    {opt.text}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-4 border-t border-gray-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
            <button
              className="flex items-center justify-center px-6 py-2 rounded-full bg-white text-[#2B6A7F] border-2 border-[#0000001A] text-base disabled:opacity-60"
              onClick={handlePrev}
              disabled={current === 0 || submitting}
            >
              <ChevronLeftIcon className="w-5 h-5 mr-4" />
              Previous
            </button>
            {current === questions.length - 1 ? (
              <button
                className="flex items-center justify-center px-8 py-2 rounded-full bg-[#D9ECF8] text-[#2B6A7F] text-base disabled:opacity-60 transition"
                onClick={() => setShowEndConfirm(true)}
                disabled={submitting}
              >
                {submitting ? "Submitting..." : "Submit"}
              </button>
            ) : (
              <button
                className="flex items-center justify-center px-8 py-2 rounded-full bg-[#D9ECF8] text-[#2B6A7F] text-base disabled:opacity-60 transition"
                onClick={handleNext}
                disabled={submitting}
              >
                Next
                <ChevronRightIcon className="w-5 h-5 ml-4" />
              </button>
            )}
          </div>
        </div>

        {/* Timer Expired Warning */}
        {timerExpired && submitting && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
              <div className="text-red-600 font-bold text-lg mb-2">
                ⏰ Time's Up!
              </div>
              <div className="mb-4 text-[#7a869a]">
                Your test time has expired. Submitting your answers now...
              </div>
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6ea6b9] mx-auto"></div>
            </div>
          </div>
        )}

        {/* Tab Switch Warning Modal */}
        {showTabWarning && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
              <div className="text-red-600 font-bold text-lg mb-2">
                ⚠️ Warning
              </div>
              <div className="mb-4 text-[#7a869a]">
                Tab switching detected! You have {3 - tabSwitchCount} warning(s)
                left. If you switch tabs again, your test will be automatically
                submitted.
              </div>
              <button
                className="px-6 py-2 rounded-lg bg-[#6ea6b9] text-white font-semibold"
                onClick={() => setShowTabWarning(false)}
              >
                I understand
              </button>
            </div>
          </div>
        )}

        {/* End Test Confirmation Modal */}
        {showEndConfirm && (
          <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-lg p-8 max-w-xs w-full text-center">
              <div className="font-bold text-lg mb-2">End Test?</div>
              <div className="mb-4 text-[#7a869a]">
                Your answers will be submitted. Are you sure you want to end the
                test?
              </div>
              <div className="flex gap-4 justify-center mt-6">
                <button
                  className="px-6 py-2 rounded-lg bg-[#e3eaf3] text-[var(--secondary-font)] font-semibold"
                  onClick={() => setShowEndConfirm(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  className="px-6 py-2 rounded-lg bg-[#6ea6b9] text-white font-semibold disabled:opacity-50"
                  onClick={handleEndTest}
                  disabled={submitting}
                >
                  {submitting ? "Submitting..." : "End Test"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
